import React, { useState, useEffect, useRef, useCallback } from "react";
import { gpsAPI, logisticsAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { MapPin, Truck, Navigation, Plus, Clock, Check, X, RefreshCw, Loader, ExternalLink } from "lucide-react";
import { fmtTime } from "../utils/dateUtils";

const CHECKPOINT_TYPES = [
  { value: "departed_kitchen", label: "Departed Kitchen", color: "text-blue-600 bg-blue-50" },
  { value: "arrived_school",   label: "Arrived at School", color: "text-[#637C64] bg-[#EDF2EE]" },
  { value: "departed_school",  label: "Departed School", color: "text-[#D98032] bg-[#FDF4EB]" },
  { value: "returned_kitchen", label: "Returned to Kitchen", color: "text-purple-600 bg-purple-50" },
];

const VEHICLE_STATUS = {
  at_kitchen:  { label: "At Kitchen", color: "text-blue-600 bg-blue-50" },
  en_route:    { label: "En Route", color: "text-[#D98032] bg-[#FDF4EB]" },
  at_school:   { label: "At School", color: "text-[#637C64] bg-[#EDF2EE]" },
  returning:   { label: "Returning", color: "text-purple-600 bg-purple-50" },
  completed:   { label: "Completed", color: "text-[#5C5147] bg-[#F4F3F0]" },
};

export default function VehicleTracking() {
  const { selectedKitchen } = useKitchen();
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [liveLocations, setLiveLocations] = useState([]);
  const [checkpoints, setCheckpoints] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [selectedRoute, setSelectedRoute] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [tracking, setTracking] = useState(false);
  const [gpsStatus, setGpsStatus] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const intervalRef = useRef(null);
  const mapRef = useRef(null);

  const fetchLive = useCallback(async () => {
    try {
      const r = await gpsAPI.getLiveLocations({ kitchen_id: selectedKitchen || undefined, date });
      setLiveLocations(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  }, [selectedKitchen, date]);

  useEffect(() => {
    Promise.all([
      logisticsAPI.getRoutes(),
      logisticsAPI.getVehicles()
    ]).then(([r, v]) => {
      setRoutes((r.data || []).filter(r => !r.route_number?.startsWith("TEST")));
      setVehicles(v.data || []);
    }).catch(e => { if (process.env.NODE_ENV !== "production") console.warn("[fetch]", e); });
    fetchLive();
    const t = setInterval(fetchLive, 30000);
    return () => clearInterval(t);
  }, [selectedKitchen, fetchLive]);

  useEffect(() => {
    if (selectedVehicle && date) fetchCheckpoints();
  }, [selectedVehicle, date]);

  const fetchCheckpoints = async () => {
    try {
      const r = await gpsAPI.getCheckpoints({ date, vehicle_number: selectedVehicle });
      setCheckpoints(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  // GPS tracking from browser
  const startTracking = () => {
    if (!navigator.geolocation) { setGpsStatus("GPS not available on this device"); return; }
    if (!selectedVehicle) { setError("Select a vehicle first"); return; }
    setTracking(true);
    setGpsStatus("Sharing location...");

    const sendLocation = () => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            await gpsAPI.updateLocation({
              vehicle_number: selectedVehicle,
              route_id: selectedRoute || null,
              route_number: routes.find(r => r.id === selectedRoute)?.route_number || "",
              kitchen_id: selectedKitchen || null,
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              status: "en_route"
            });
            setGpsStatus(`Location shared at ${new Date().toLocaleTimeString("en-IN")}`);
            fetchLive();
          } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
        },
        (err) => setGpsStatus(`GPS error: ${err.message}`),
        { enableHighAccuracy: true }
      );
    };

    sendLocation();
    intervalRef.current = setInterval(sendLocation, 30000);
  };

  const stopTracking = () => {
    setTracking(false);
    setGpsStatus("Tracking stopped");
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
  };

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  const addCheckpoint = async () => {
    setError("");
    try {
      await gpsAPI.addCheckpoint({
        vehicle_number: form.vehicle_number || selectedVehicle,
        route_id: selectedRoute || null,
        route_number: routes.find(r => r.id === selectedRoute)?.route_number || "",
        kitchen_id: selectedKitchen || null,
        school_name: form.school_name || "",
        checkpoint_type: form.checkpoint_type || "departed_kitchen",
        notes: form.notes || ""
      });
      setModal(null); setForm({});
      fetchCheckpoints();
      setSuccess("Checkpoint logged!"); setTimeout(() => setSuccess(""), 2000);
    } catch (e) { setError(formatError(e)); }
  };

  const cpConfig = (type) => CHECKPOINT_TYPES.find(t => t.value === type) || CHECKPOINT_TYPES[0];
  const vsConfig = (status) => VEHICLE_STATUS[status] || VEHICLE_STATUS.en_route;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="vehicle-tracking">
      <div>
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Vehicle Tracking</h1>
        <p className="text-sm text-[#8A7E73] mt-0.5">GPS location sharing + manual checkpoints for route progress</p>
      </div>
      <a href="/driver" target="_blank" rel="noopener noreferrer"
        className="bg-blue-600 text-white rounded-lg px-4 py-2.5 font-bold text-sm flex items-center gap-2 hover:bg-blue-700">
        <Navigation size={15} /> Driver GPS Page
      </a>

      {success && <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-lg px-4 py-2.5 text-sm text-[#637C64]">{success}</div>}
      {error && <div className="bg-[#FBEAE7] border border-[#C2624E]/30 rounded-lg px-4 py-2.5 text-sm text-[#C2624E]">{error}</div>}

      {/* GPS Sharing panel */}
      <div className="bg-gradient-to-r from-blue-50 to-[#E8956D]/5 border border-blue-200 rounded-2xl p-5">
        <h3 className="font-bold text-[#3A322C] mb-3 flex items-center gap-2"><Navigation size={18} className="text-blue-600" /> Live GPS Tracking (Driver's Phone)</h3>
        <p className="text-xs text-[#8A7E73] mb-4">Open this app on the driver's phone, select their vehicle, and click "Start Sharing" — location updates every 30 seconds automatically.</p>
        <div className="flex gap-3 flex-wrap items-end">
          <div>
            <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vehicle</label>
            <select value={selectedVehicle} onChange={e => setSelectedVehicle(e.target.value)}
              className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]">
              <option value="">Select Vehicle</option>
              {vehicles.map(v => <option key={v.id} value={v.vehicle_number}>{v.vehicle_number}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Route</label>
            <select value={selectedRoute} onChange={e => setSelectedRoute(e.target.value)}
              className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]">
              <option value="">Select Route</option>
              {routes.map(r => <option key={r.id} value={r.id}>{r.route_number} Route</option>)}
            </select>
          </div>
          {!tracking ? (
            <button onClick={startTracking} disabled={!selectedVehicle}
              className="bg-blue-600 text-white rounded-xl px-5 py-2.5 font-bold text-sm flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50">
              <Navigation size={15} /> Start Sharing Location
            </button>
          ) : (
            <button onClick={stopTracking} className="bg-[#C2624E] text-white rounded-xl px-5 py-2.5 font-bold text-sm flex items-center gap-2 hover:bg-[#a85040]">
              <X size={15} /> Stop Tracking
            </button>
          )}
        </div>
        {gpsStatus && (
          <p className={`text-xs mt-2 font-medium ${tracking ? "text-blue-600" : "text-[#8A7E73]"} flex items-center gap-1`}>
            {tracking && <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse inline-block" />}
            {gpsStatus}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Live locations */}
        <div className="bg-white border border-[#E5E1DB] rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E1DB]">
            <h3 className="font-bold text-[#3A322C]">Live Vehicle Positions</h3>
            <button onClick={fetchLive} className="text-[#8A7E73] hover:text-[#E8956D] p-1"><RefreshCw size={15} /></button>
          </div>
          <div className="divide-y divide-[#F4F3F0] max-h-72 overflow-y-auto">
            {liveLocations.length === 0 ? (
              <div className="text-center py-10 text-[#8A7E73] text-sm">No active vehicles today</div>
            ) : liveLocations.map(loc => {
              const vs = vsConfig(loc.status);
              return (
                <div key={loc.id} className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Truck size={18} className="text-[#E8956D] flex-shrink-0" />
                      <div>
                        <p className="font-bold text-[#3A322C] text-sm">{loc.vehicle_number}</p>
                        {loc.route_number && <p className="text-xs text-[#8A7E73]">Route {loc.route_number}</p>}
                      </div>
                    </div>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${vs.color}`}>{vs.label}</span>
                  </div>
                  {loc.latitude && loc.longitude && (
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-xs text-[#8A7E73]">{loc.latitude.toFixed(4)}, {loc.longitude.toFixed(4)}</p>
                      <a href={`https://maps.google.com/?q=${loc.latitude},${loc.longitude}`} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-blue-600 font-bold flex items-center gap-1 hover:underline">
                        View on Maps <ExternalLink size={10} />
                      </a>
                    </div>
                  )}
                  {loc.timestamp && (
                    <p className="text-[10px] text-[#C4BAAF] mt-1">Updated: {fmtTime(loc.timestamp)}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Checkpoints */}
        <div className="bg-white border border-[#E5E1DB] rounded-2xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E1DB]">
            <div>
              <h3 className="font-bold text-[#3A322C]">Route Checkpoints</h3>
              <p className="text-xs text-[#8A7E73]">Manual milestones logged by coordinator</p>
            </div>
            <div className="flex gap-2 items-center">
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                className="border border-[#E5E1DB] rounded-lg px-2 py-1 text-xs outline-none focus:border-[#E8956D]" />
              <button onClick={() => { setForm({ checkpoint_type: "departed_kitchen" }); setModal("checkpoint"); setError(""); }}
                className="text-xs bg-[#E8956D]/10 text-[#E8956D] px-3 py-1.5 rounded-lg font-bold hover:bg-[#E8956D]/20 flex items-center gap-1">
                <Plus size={12} /> Log
              </button>
            </div>
          </div>
          <div className="px-4 py-3">
            {selectedVehicle && (
              <p className="text-xs text-[#8A7E73] mb-3">Showing: {selectedVehicle || "all vehicles"}</p>
            )}
            {checkpoints.length === 0 ? (
              <div className="text-center py-8 text-[#8A7E73] text-sm">No checkpoints logged today</div>
            ) : (
              <div className="space-y-2">
                {checkpoints.map((cp, i) => {
                  const cpCfg = cpConfig(cp.checkpoint_type);
                  return (
                    <div key={cp.id || i} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <div className={`w-2 h-2 rounded-full ${i === checkpoints.length - 1 ? "bg-[#E8956D]" : "bg-[#E5E1DB]"}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${cpCfg.color}`}>{cpCfg.label}</span>
                          <span className="text-xs text-[#8A7E73]">{cp.vehicle_number}</span>
                        </div>
                        {cp.school_name && <p className="text-xs text-[#5C5147] mt-0.5">{cp.school_name}</p>}
                        <p className="text-[10px] text-[#C4BAAF]">{cp.timestamp ? fmtTime(cp.timestamp) : ""} · {cp.logged_by_name}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Checkpoint Modal */}
      {modal === "checkpoint" && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-fade-in-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
              <h2 className="font-bold text-lg text-[#3A322C]">Log Checkpoint</h2>
              <button onClick={() => setModal(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Vehicle *</label>
                <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.vehicle_number || selectedVehicle || ""} onChange={e => setForm({ ...form, vehicle_number: e.target.value })}>
                  <option value="">Select</option>
                  {vehicles.map(v => <option key={v.id} value={v.vehicle_number}>{v.vehicle_number}</option>)}
                </select></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Checkpoint Type *</label>
                <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.checkpoint_type || ""} onChange={e => setForm({ ...form, checkpoint_type: e.target.value })}>
                  {CHECKPOINT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select></div>
              {(form.checkpoint_type === "arrived_school" || form.checkpoint_type === "departed_school") && (
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">School Name</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.school_name || ""} onChange={e => setForm({ ...form, school_name: e.target.value })} /></div>
              )}
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label>
                <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
              {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
              <div className="flex gap-3">
                <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
                <button onClick={addCheckpoint} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Log Checkpoint</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
